(() => {
  const clamp = value => Math.max(0,Math.min(1,value));
  const phase = (value,start,end) => { const x=clamp((value-start)/(end-start)); return x*x*(3-2*x); };
  class HxRangeStory extends HTMLElement {
    connectedCallback() {
      if(this.controller)return;
      this.controller=new AbortController();
      const signal=this.controller.signal;
      this.stage=this.querySelector('.hx-story-stage');
      this.content=this.querySelector('.hx-story-content');
      this.motion=matchMedia('(prefers-reduced-motion:reduce)');
      if(!this.stage||!this.content)return;
      this.queue=()=>{if(!this.completed&&!this.frame)this.frame=requestAnimationFrame(()=>this.render());};
      this.resize=()=>{cancelAnimationFrame(this.measureFrame);this.measureFrame=requestAnimationFrame(()=>this.measure());};
      this.finish=()=>{
        if(this.hasAttribute('data-story-covering')||this.completed)return;
        this.furthest=1;this.progress=1;this.render();
      };
      document.addEventListener('click',event=>{
        const link=event.target.closest('.hx-hero-reviews, .hx-community-return a, [data-hx-story-skip]');
        if(!link||event.button!==0||event.metaKey||event.ctrlKey||event.shiftKey||event.altKey)return;
        const target=document.getElementById(link.hash.slice(1));
        if(!target)return;
        event.preventDefault();
        // Collapse the introduction before calculating the review destination.
        if(!this.completed&&(link.hasAttribute('data-hx-story-skip')||(this.compareDocumentPosition(target)&Node.DOCUMENT_POSITION_FOLLOWING))){
          this.furthest=1;this.progress=1;this.render();
        }
        requestAnimationFrame(()=>{
          target.scrollIntoView({behavior:this.motion.matches?'instant':'smooth',block:'start'});
          history.replaceState(history.state,'',link.hash);
        });
      },{capture:true,signal});
      this.content.addEventListener('pointerdown',this.finish,{passive:true,signal});
      this.content.addEventListener('focusin',this.finish,{signal});
      window.addEventListener('scroll',this.queue,{passive:true,signal});
      window.addEventListener('resize',this.resize,{passive:true,signal});
      window.addEventListener('pageshow',this.resize,{signal});
      this.motion.addEventListener('change',this.resize,{signal});
      this.observer=new ResizeObserver(this.resize);
      this.observer.observe(this.content);
      document.fonts?.ready.then(()=>{if(this.isConnected)this.resize();});
      this.measure();
    }
    uncover(visible) {
      const covering=!visible;
      if(this.hasAttribute('data-story-covering')===covering)return;
      this.toggleAttribute('data-story-covering',covering);
      this.content.inert=covering;
      this.dispatchEvent(new CustomEvent('hx:story-visibility'));
    }
    measure() {
      this.measureFrame=0;
      if(!this.isConnected)return;
      if(this.completed){this.uncover(true);return;}
      if(this.motion.matches){
        this.removeAttribute('data-story-enhanced');
        this.uncover(true);
        this.dataset.storyPhase='static';
        return;
      }
      this.setAttribute('data-story-enhanced','');
      const viewport=window.innerHeight;
      this.distance=Math.round(viewport*(window.innerWidth<750?.45:.65));
      this.style.setProperty('--hx-story-viewport',`${viewport}px`);
      this.style.setProperty('--hx-story-distance',`${this.distance}px`);
      this.style.setProperty('--hx-story-height',`${Math.max(viewport,this.stage.offsetHeight)}px`);
      this.start=this.getBoundingClientRect().top+window.scrollY;
      this.furthest=Math.max(this.furthest||0,clamp((window.scrollY-this.start)/this.distance));
      this.progress=Math.max(this.progress||0,this.furthest);
      this.render();
    }
    render() {
      this.frame=0;
      if(!this.isConnected||this.motion.matches||!this.distance)return;
      const raw=(window.scrollY-this.start)/this.distance;
      this.furthest=Math.max(this.furthest||0,clamp(raw));
      const target=this.furthest>=.98?1:this.furthest;
      this.progress=raw>=1||this.completed?target:this.progress+(target-this.progress)*.22;
      if(Math.abs(target-this.progress)<.0004)this.progress=target;
      const p=this.progress;
      const dailyExit=phase(p,.20,.32);
      const formulaEnter=phase(p,.33,.46);
      const pullback=phase(p,.48,.78);
      const reveal=phase(p,.48,.80);
      const values={
        'daily-opacity':1-dailyExit,
        'daily-scale':1.04-phase(p,0,.25)*.04-dailyExit*.09,
        'daily-y':`${-dailyExit*30}px`,
        'daily-glow':.16+Math.sin(phase(p,0,.35)*Math.PI)*.45,
        'formula-opacity':formulaEnter*(1-phase(p,.50,.66)),
        'formula-scale':1.1-formulaEnter*.1-pullback*.66,
        'formula-y':`${(1-formulaEnter)*35-pullback*110}px`,
        'formula-glow':.16+Math.sin(phase(p,.28,.86)*Math.PI)*.5,
        'reveal':reveal,
        'clip':`${(1-reveal)*7}%`,
        'radius':`${(1-reveal)*32}px`,
        'cover':1-pullback,
        'orbit-scale':1+formulaEnter*.12-pullback*.55,
        'horizon':1-pullback*.7,
        'progress':p,
        'cue-y':`${Math.sin(p*Math.PI*2)*7}px`
      };
      for(const [key,value] of Object.entries(values)){
        const property=`--hx-story-${key}`,text=String(value);
        if(this.style.getPropertyValue(property)!==text)this.style.setProperty(property,text);
      }
      const storyPhase=p<.3?'daily':p<.65?'formula':p<.98?'reveal':'complete';
      if(this.dataset.storyPhase!==storyPhase)this.dataset.storyPhase=storyPhase;
      this.uncover(reveal>0);
      if(p===1)this.complete();
      if(this.progress!==target)this.queue();
    }
    complete() {
      if(this.completed)return;
      const position=this.stage.getBoundingClientRect().top;
      this.completed=true;
      this.setAttribute('data-story-complete','');
      this.uncover(true);
      const shift=this.stage.getBoundingClientRect().top-position;
      if(Math.abs(shift)>.5)window.scrollBy({top:shift,behavior:'instant'});
      this.dispatchEvent(new CustomEvent('hx:story-visibility'));
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);cancelAnimationFrame(this.measureFrame);
      this.controller?.abort();this.controller=null;this.observer?.disconnect();
      if(this.content)this.content.inert=false;
    }
  }
  if(!customElements.get('hx-range-story'))customElements.define('hx-range-story',HxRangeStory);
})();

